import React  from 'react';

// Material UI
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';

// Components
import { SchemaDrivenReportCard } from '../../Report/SchemaReportCard/schema-driven-report-card.component';
import { useGetApplicableReportCardSchema } from '../../../lib/services/report-card-schema.service';
import { useGetReportCardData } from '../../../lib/services/report-card-data.service';
import { readReportCardUnavailable } from '../../../lib/helpers/graphql-error.helpers';
import { ReportCardUnavailable } from '../../ReportCardAccess/report-card-unavailable.component';

interface IReportCard {
  player?: IPlayer;
  playerAssessment?: IPlayerAssessment;
  assessmentReportCards: IPlayerAssessment[];
  loading: boolean;
  loadingCategories: boolean;
  categories: IWeightCategory[];
  summaryBySkillId: { [key: string]: IScoreSummary };
  bestSummaryBySkillId: { [key: string]: IScoreSummary };
  summaryByCategoryId: { [key: string]: IScoreSummary };
  groupSummaryBySkillId: { [key: string]: IScoreSummary };
  code: string;
  schemaId?: string; // Optional schema ID to use different layouts
  token?: string;
}
export const ReportCard: React.FC<IReportCard> = ({
  player,
  playerAssessment,
  loading,
  token
}) => {

  const { data: databaseSchema, loading: schemaLoading, error: schemaError } = useGetApplicableReportCardSchema({
    assessmentId: playerAssessment?.assessment._id,
    ageGroupId: playerAssessment?.ageGroup._id,
    positionId: playerAssessment?.position._id,
    token
  });

  const { data: reportCardData, loading: dataLoading, error: dataError } = useGetReportCardData({
    playerAssessmentId: playerAssessment?._id,
    token
  });

  if (readReportCardUnavailable(schemaError) || readReportCardUnavailable(dataError)) return <ReportCardUnavailable />;

  // Handle schema and data access errors
  if (schemaError || dataError) {
    const errorMessage = schemaError?.message || dataError?.message || '';

    if (token && (errorMessage.includes('Invalid or expired access token') ||
                  errorMessage.includes('Insufficient permissions') ||
                  errorMessage.includes('Forbidden'))) {
      return (
        <Box p={4} textAlign="center">
          <Typography variant="h5" gutterBottom color="error">
            Cannot Access Report Card Data
          </Typography>
          <Typography variant="body1" gutterBottom>
            Your access token doesn't have permission to view this report card's detailed data or schema.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            This may happen if the token was generated for a different player or has expired. Please contact the organization for a new access link.
          </Typography>
        </Box>
      );
    }

    return (
      <Box p={4} textAlign="center">
        <Typography variant="h5" gutterBottom color="error">
          Error Loading Report Card
        </Typography>
        <Typography variant="body1" gutterBottom>
          There was an error loading the report card data or schema.
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {errorMessage || 'Please try again later or contact support for assistance.'}
        </Typography>
      </Box>
    );
  }

  if (schemaLoading || dataLoading || (!player && !playerAssessment && loading)) return <Typography>Loading...</Typography>;
  if (!player || !playerAssessment) return <Typography>Unable to show your report card at this time. Please try again later</Typography>;

  // const getOutboundLink = function(url: string) {
  //   gtag('event', 'click', {
  //     'event_category': 'outbound',
  //     'event_label': url,
  //     'transport_type': 'beacon',
  //     'event_callback': function(){
  //       // @ts-ignore
  //       document.location = url;
  //     }
  //   });

  //   setTimeout(() => {
  //     // @ts-ignore
  //     document.location = url;
  //   }, 3000);
  // }

  // Use database report card data or fallback to empty object
  const reportData = reportCardData?.data || {};

  if (schemaError) return <Typography>Error loading report card template</Typography>;
  if (dataError) return <Typography>Error loading report card data</Typography>;
  if (schemaLoading || dataLoading) return <Typography>Loading report card...</Typography>;
  if (!databaseSchema) return <Typography>No report card template found</Typography>;
  if (!reportCardData) return <Typography>No report card data found</Typography>;

  // Get the organization/association logo from the company data
  const organizationLogo = playerAssessment?.assessment?.company?.logo;

  // Get logo height from schema props (fallback) or data (priority)
  const organizationLogoHeight = reportData?.organizationLogoHeight || databaseSchema?.props?.organizationLogoHeight;

  // Use schema-driven report card system with database-loaded schema
  return <SchemaDrivenReportCard
    schema={databaseSchema}
    player={player}
    playerAssessment={playerAssessment}
    data={reportData}
    organizationLogo={organizationLogo}
    organizationLogoHeight={organizationLogoHeight}
  />
}
